import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Football: React.FC = () => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/pages/activity/football`);
      if (response.data.success) {
        const data = response.data.data;
        
        // If activity is inactive, redirect to activities page
        if (!data.isActive) {
          navigate('/activities');
          return;
        }
        
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching football settings:', error);
      // If error, redirect to activities page
      navigate('/activities');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">⚽</div>
            <h1 className="text-4xl font-bold mb-4">{settings.title || 'Football'}</h1>
            {settings.description && (
              <p className="text-lg text-gray-600 mt-4">{settings.description}</p>
            )}
          </div>
          
          <div className="space-y-6">
            {/* Schedule */}
            {(settings.schedule?.day || settings.schedule?.time) && (
              <div>
                <h2 className="text-2xl font-bold mb-3">Schedule</h2>
                {settings.schedule.day && (
                  <p className="text-lg"><strong>Day:</strong> {settings.schedule.day}</p>
                )}
                {settings.schedule.time && (
                  <p className="text-lg"><strong>Time:</strong> {settings.schedule.time}</p>
                )}
              </div>
            )}

            {/* Location */}
            {settings.location && (
              <div>
                <h2 className="text-2xl font-bold mb-3">Location</h2>
                <p className="text-lg">{settings.location}</p>
              </div>
            )}

            {/* Details */}
            {settings.details && (
              <div>
                <h2 className="text-2xl font-bold mb-3">Details</h2>
                <div className="text-lg whitespace-pre-line">
                  {settings.details.split('\n').map((line: string, idx: number) => 
                    line.trim() ? <p key={idx} className="mb-2">{line}</p> : null
                  )}
                </div>
              </div>
            )}

            {/* Image Gallery */}
            {settings.images && settings.images.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {settings.images.map((img: string, idx: number) => (
                    <div key={idx} className="overflow-hidden rounded-lg shadow-md">
                      <img
                        src={`http://localhost:5000${img}`}
                        alt={`${settings.title} ${idx + 1}`}
                        className="w-full h-64 object-cover hover:scale-110 transition duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            {(settings.contactEmail || settings.contactPhone) && (
              <div>
                <h2 className="text-2xl font-bold mb-3">Contact</h2>
                <p className="text-lg mb-2">For more information or to join:</p>
                <div className="flex flex-col gap-2">
                  {settings.contactEmail && (
                    <a href={`mailto:${settings.contactEmail}`} className="text-primary hover:text-primary-dark">
                      📧 {settings.contactEmail}
                    </a>
                  )}
                  {settings.contactPhone && (
                    <a href={`tel:${settings.contactPhone}`} className="text-primary hover:text-primary-dark">
                      📞 {settings.contactPhone}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Football;
